
import { useState, useCallback, useEffect, useRef } from 'react';
import { getCachedData, setCachedData, removeCachedData } from '@/utils/cacheUtils';
import { Database } from '@/integrations/supabase/types';

// Constants
const SUBSCRIPTION_CACHE_KEY = "user_subscription_cache";
const SUBSCRIPTION_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export type SubscriptionData = Database["public"]["Tables"]["subscribers"]["Row"] | null;

/**
 * Hook for managing subscription data and status
 * @param userId The user ID to check subscription for
 */
export const useSubscription = (userId: string | undefined) => {
  const [subscription, setSubscription] = useState<SubscriptionData>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Set default unlimited subscription for all users
  useEffect(() => {
    if (!userId) {
      setSubscription(null);
      removeCachedData(SUBSCRIPTION_CACHE_KEY);
      return;
    }
    
    // Create default free subscription data
    const freeSubscriptionData = {
      id: '',
      user_id: userId,
      email: '',
      subscribed: true,
      subscription_tier: 'premium',
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      subscription_end: null,
      stripe_customer_id: null
    };
    
    setSubscription(freeSubscriptionData);
    setCachedData(SUBSCRIPTION_CACHE_KEY, freeSubscriptionData, SUBSCRIPTION_CACHE_TTL);
  }, [userId]);

  // Simplified refresh function (no longer contacts any API)
  const refreshSubscriptionStatus = useCallback(async (forceRefresh = false) => {
    // All users get premium access now, no need to check status
    return;
  }, []);

  // Always return premium status
  const isPremium = true;

  return {
    subscription,
    isRefreshing,
    isPremium,
    refreshSubscriptionStatus,
    refreshAttempts: 0
  };
};

// Helper function to clear subscription cache
export const clearSubscriptionCache = () => removeCachedData(SUBSCRIPTION_CACHE_KEY);
